import React from "react";
import { Helmet } from "react-helmet-async";
import Container from "../components/layout/Container";
import PreparationCheckForm from "../components/preparationCheck/PreparationCheckForm";
import PerformanceBufferSection from "../components/preparation/PerformanceBufferSection";

const pageUrl = "https://missionmovement.nl/voorbereiding-check";
const ogImage = "https://missionmovement.nl/img/defensiekeuring.png";

const methodCards = [
  {
    title: "Startniveau",
    text: "Eerst kijken waar je nu staat. Je nulmeting, pijnsignalen, trainingservaring en huidige belastbaarheid bepalen je route.",
  },
  {
    title: "Functie-eisen",
    text: "Een brandweertraject vraagt iets anders dan een militaire richting. Je training moet aansluiten op de realiteit van jouw doel.",
  },
  {
    title: "Belastbaarheid",
    text: "Lopen, dragen, tillen, zwemmen, herstellen en opnieuw leveren. Je lichaam moet wennen aan wat straks vaker terugkomt.",
  },
  {
    title: "Progressie & bijsturen",
    text: "Meten, volgen en aanpassen wanneer je lichaam informatie geeft. Niet blind blijven rammen, maar gericht blijven bouwen.",
  },
];

const audienceItems = [
  "Kandidaten richting Defensie",
  "Kandidaten richting Mariniers, KCT, Luchtmobiel of andere fysieke richtingen",
  "Kandidaten richting politie, brandweer of KMar",
  "Mensen die willen weten of hun training genoeg richting heeft",
  "Mensen die niet alleen de ondergrens willen halen, maar met meer marge willen starten",
];

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Gratis Voorbereidingscheck",
  url: pageUrl,
  inLanguage: "nl-NL",
  description:
    "Doe de gratis Mission Movement voorbereidingscheck en ontdek waar jouw grootste aandachtspunten liggen richting Defensie of veiligheidsdiensten.",
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
};

const PreparationCheckLanding = () => {
  const scrollToCheck = () => {
    const checkSection = document.getElementById("voorbereidings-check");

    if (!checkSection) return;

    checkSection.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-mmPage text-mmText">
      <Helmet>
        <title>Gratis Voorbereidingscheck | Mission Movement</title>
        <meta
          name="description"
          content="Doe de gratis Mission Movement voorbereidingscheck en ontdek waar jouw grootste aandachtspunten liggen richting Defensie of veiligheidsdiensten."
        />
        <meta
          property="og:title"
          content="Gratis Voorbereidingscheck | Mission Movement"
        />
        <meta
          property="og:description"
          content="Ontdek met de gratis Mission Movement voorbereidingscheck waar jouw training richting Defensie of veiligheidsdiensten nog aandacht nodig heeft."
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <section className="relative min-h-[calc(100svh-4rem)] md:min-h-[640px] lg:h-[calc(100vh-80px)] lg:min-h-0 lg:max-h-[calc(100vh-80px)] flex items-center overflow-hidden border-b border-mmBorder">
        <img
          src="/img/defensiekeuring.png"
          alt="Kandidaten tijdens fysieke voorbereiding"
          className="absolute inset-0 h-full w-full object-cover object-center"
          width={858}
          height={536}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 mm-heroOverlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/35 to-mmPage" />

        <Container className="relative z-10 py-16 md:py-20 lg:py-8 xl:py-10">
          <div className="max-w-4xl">
            <p className="mb-4 lg:mb-3 font-display uppercase tracking-[0.24em] text-mmAccent text-xs md:text-sm">
              Gratis voorbereidings-check
            </p>
            <h1 className="font-display uppercase tracking-[0.055em] text-white text-[42px] sm:text-[54px] md:text-[76px] lg:text-[68px] xl:text-[74px] leading-[0.9] max-w-[10ch] sm:max-w-[12ch] md:max-w-[13ch] lg:max-w-[14ch]">
              Weet waar je staat voordat je begint.
            </h1>
            <div className="h-[3px] w-14 rounded-full bg-mmAccent mt-5 mb-6 lg:mt-4 lg:mb-4" />
            <div className="max-w-2xl space-y-5 lg:space-y-3">
              <p className="text-white text-lg md:text-2xl lg:text-xl xl:text-2xl leading-snug font-semibold">
                Doe de gratis Mission Movement voorbereidings-check en ontdek
                of jouw huidige training aansluit op wat jouw richting straks
                van je vraagt.
              </p>
              <p className="text-white/82 text-base md:text-lg lg:text-base xl:text-lg leading-relaxed lg:leading-7">
                Veel kandidaten trainen hard, maar niet altijd gericht. Deze
                check helpt je kijken naar je startniveau, fysieke basis,
                belastbaarheid, herstel en trainingsstructuur. Niet om je af te
                keuren, maar om duidelijk te maken waar je aandacht naartoe
                moet.
              </p>
            </div>
            <button
              type="button"
              onClick={scrollToCheck}
              className="mm-btnPrimary mt-8 lg:mt-6 px-8"
            >
              Start de gratis check
            </button>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl mb-10">
            <h2 className="mm-h2 mb-6">Voorbereiding met richting.</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Mission Movement helpt kandidaten richting Defensie en
              veiligheidsdiensten om gestructureerd te bouwen aan fysieke
              basis, belastbaarheid en inzetbaarheid. Niet trainen voor
              willekeurige fitheid, maar gericht werken vanuit jouw startpunt
              naar wat jouw richting vraagt.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {methodCards.map((card, index) => (
              <article key={card.title} className="mm-card rounded-lg p-5">
                <div className="mb-5 flex h-9 w-9 items-center justify-center rounded-full border border-mmBorderStrong text-sm font-semibold text-mmAccent">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-lg mb-3">
                  {card.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {card.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <PerformanceBufferSection variant="check" compact className="py-12 md:py-16" />

      <section
        id="voorbereidings-check"
        className="relative scroll-mt-24 overflow-hidden bg-[#15130d] py-16 md:py-20 border-y border-yellow-500/20"
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at top right, rgba(255,204,0,0.10), transparent 35%)",
          }}
          aria-hidden="true"
        />
        <Container>
          <div className="relative z-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(320px,460px)] lg:items-start">
            <div className="max-w-3xl">
              <p className="font-display uppercase tracking-[0.2em] text-mmAccent text-xs md:text-sm mb-4">
                Gratis check
              </p>
              <h2 className="mm-h2 mb-5">
                Doe de gratis voorbereidings-check.
              </h2>
              <p className="text-white text-lg md:text-xl leading-snug font-semibold mb-5">
                Beantwoord een aantal korte vragen en ontvang straks een
                persoonlijk resultaat per e-mail.
              </p>
              <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
                De check kijkt naar onderdelen zoals hardlopen, kracht,
                bepakking, herstel, blessures en trainingsstructuur. Op basis
                van je antwoorden krijg je inzicht in waar jouw voorbereiding
                waarschijnlijk nog marge mist.
              </p>
            </div>

            <PreparationCheckForm />
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <h2 className="mm-h2 mb-5">Voor wie is deze check?</h2>
              <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
                Voor kandidaten die hun voorbereiding serieus willen richten
                voordat selectie, opleiding of testmoment dichterbij komt.
              </p>
            </div>

            <div className="space-y-3">
              {audienceItems.map((item) => (
                <div
                  key={item}
                  className="flex gap-3 rounded-lg border border-mmBorder bg-mmSurface px-4 py-3"
                >
                  <span
                    className="mt-2 h-2 w-2 shrink-0 rounded-full bg-mmAccent"
                    aria-hidden="true"
                  />
                  <p className="text-sm md:text-base text-mmTextMuted leading-relaxed">
                    {item}
                  </p>
                </div>
              ))}

              <p className="pt-4 text-xs md:text-sm leading-relaxed text-mmTextMuted">
                De check is geen medische beoordeling en vervangt geen
                officiële selectie-eis. Het is een praktisch hulpmiddel om
                beter te begrijpen waar je nu staat en waar je voorbereiding
                aandacht nodig heeft.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface">
        <Container>
          <div className="max-w-3xl">
            <h2 className="font-display uppercase tracking-[0.055em] text-white text-[34px] md:text-[54px] leading-none mb-5">
              Niet gokken. Eerst kijken waar je staat.
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed mb-8">
              Serieuze voorbereiding begint niet met zomaar harder trainen. Het
              begint met weten waar je nu staat, begrijpen wat jouw richting
              vraagt en gericht bouwen aan wat nog ontbreekt.
            </p>
            <button
              type="button"
              onClick={scrollToCheck}
              className="mm-btnPrimary px-8"
            >
              Start de gratis check
            </button>
          </div>
        </Container>
      </section>
    </main>
  );
};

export default PreparationCheckLanding;
